import { TitleCasePipe } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { SelectionChangedEvent } from 'ag-grid-community';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { Endpoint, HelpIndex, MessageType, UpdateStatusType } from '../../../models/enums';
import { CellTemplateType, GenericColDef, GridContext, GridRowSelectionType } from '../../../models/grid-context';
import { Job } from '../../../models/job';
import { Project } from '../../../models/project';
import { RestService } from '../../../services/rest/rest.service';
import { GenericGridComponent } from '../../generic-grid/generic-grid.component';
import * as _ from 'underscore';
import { Subscription } from 'rxjs';
import { CommonService } from '../../../services/common/common.service';

enum ServiceTable {
    REALM_NODES = 0,
    PROJECTS = 1,
    JOBS = 2
}

@Component({
    standalone: false,
    selector: 'res-change-service-modal',
    templateUrl: './change-service-modal.component.html',
    styleUrls: ['./change-service-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class ChangeServiceModalComponent extends BaseModalComponent implements OnInit, AfterViewInit, OnDestroy {
    static readonly ServiceTable = ServiceTable;
    readonly ServiceTable = ChangeServiceModalComponent.ServiceTable;

    @ViewChild('services', { static: true }) servicesGrid: GenericGridComponent;
    @ViewChild('realmnodes', { static: true }) realmNodesGrid: GenericGridComponent;
    @ViewChild('projects', { static: true }) projectsGrid: GenericGridComponent;
    @ViewChild('jobs', { static: true }) jobsGrid: GenericGridComponent;

    modalAutomationId = 'change-service';
    modalHelpIndex = HelpIndex.sys_projects_change_service;
    primaryButtonLabel = 'OK';

    projectsRequestInfo = {
        endpoint: Endpoint.serviceManager_listProjectsForService,
        responseName: 'projects',
        data: null
    }

    project: Project;
    currentService: string;
    titleBar: string;
    private services: Array<any>;
    private allNodes: Array<any>;
    private allJobs: Array<any>;
    private selectedService = null;
    private realmNodeSubscription: Subscription;
    private projectHandles: Array<any> = null;

    gridContext = new GridContext({
        sortAscending: true,
        sortAscendingFieldName: 'descending',
        sortByFieldName: 'sortByFilter',
        sortBy: 'serviceName'
    });

    rowSelection = GridRowSelectionType.single;

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Service Name',
            field: 'serviceName',
            lockVisible: true,
            minWidth: 60, width: 200,
            automationId: this.modalAutomationId,
            sortBy: 'serviceName',
            defaultSortBy: 'serviceName'
        },
        {
            headerName: 'Service Description',
            field: 'description',
            lockVisible: true,
            minWidth: 60, width: 300,
            automationId: this.modalAutomationId
        },
        {
            headerName: 'Realm Nodes',
            field: 'nodeCount',
            lockVisible: true,
            minWidth: 60, width: 100,
            automationId: this.modalAutomationId,
            sortBy: 'nodeCount'
        },
        {
            headerName: 'Projects',
            field: 'projectCount',
            lockVisible: true,
            minWidth: 60, width: 70,
            automationId: this.modalAutomationId,
            sortBy: 'projectCount'
        },
        {
            headerName: 'Jobs',
            field: 'activeJobCount',
            lockVisible: true,
            minWidth: 50, width: 50,
            automationId: this.modalAutomationId,
            sortBy: 'activeJobCount'
        }
    );

    readonly realmColDefs = new Array<GenericColDef>(
        {
            headerName: 'Node Name',
            field: 'nodeName',
            lockVisible: true,
            minWidth: 60, width: 290,
            automationId: this.modalAutomationId,
        },
        {
            headerName: 'Node Type',
            field: 'analyticNodeMode',
            lockVisible: true,
            minWidth: 60, width: 160,
            automationId: this.modalAutomationId,
        }
    );

    readonly projectsColDefs = new Array<GenericColDef>(
        {
            headerName: 'Project Name',
            field: 'name',
            lockVisible: true,
            minWidth: 60, width: 220,
            automationId: this.modalAutomationId,
        },
        {
            headerName: 'Organization',
            field: 'orgName',
            lockVisible: true,
            minWidth: 60, width: 230,
            automationId: this.modalAutomationId,
        }
    );

    readonly jobsColDefs = new Array<GenericColDef>(
        {
            headerName: 'Operation Status / Description',
            field: 'description',
            lockVisible: true,
            minWidth: 60, width: 350,
            cellRenderer: 'gridWorkbasketActionsCellRenderer',
            automationId: this.modalAutomationId,
        },
        {
            headerName: 'User',
            field: 'userName',
            lockVisible: true,
            minWidth: 60, width: 100,
            automationId: this.modalAutomationId,
        },
        {
            headerName: 'Project Name',
            field: 'projectName',
            lockVisible: true,
            minWidth: 60, width: 220,
            automationId: this.modalAutomationId,
        }

    );

    activeTable = ServiceTable.REALM_NODES;
    private entityChangedEvent: Subscription;

    constructor(private titleCasePipe: TitleCasePipe,
        private modalService: BsModalService,
        private changeServiceModalRef: BsModalRef,
        private commonService: CommonService,
        private restService: RestService) {
        super(modalService, changeServiceModalRef);
        this.entityChangedEvent = this.commonService.getMessage(MessageType.entityChanged).subscribe((data) => {
            if (data === UpdateStatusType.PROJECT) {
                this.refreshProject();
            }
        });
    }

    ngOnInit(): void {
        if (this.modalData?.project) {
            this.project = this.modalData.project;
            this.titleBar = '\'' + this.project.name + (this.project.description ? ' - ' + this.project.description : '') + '\'';
        } else {
            this.projectHandles = this.modalData?.projectHandles;
            this.titleBar = 'Selected Projects';
        }
        this.currentService = this.modalData.currentService;
        this.realmNodeSubscription = this.restService.postWithObservable(Endpoint.realmManager_listNodes, { systemScope: true }).subscribe((response) => {
            this.allNodes = _.map(response?.nodes, node => {
                if (node.analyticNodeMode !== 'OCR') {
                    node.analyticNodeMode = this.titleCasePipe.transform(node.analyticNodeMode);
                }
                return node;
            });
            this.updateSelectedServiceNodes();
        });
        this.refreshProject();
    }

    ngAfterViewInit(): void {
        this.services = _.reject(this.modalData.services, service => service.serviceName === 'Internal Digital Reef Service');
        this.servicesGrid.setData(this.services);

        if (this.modalData?.currentService) {
            setTimeout(() => {
                this.preselectService(this.modalData.currentService);
            });

            this.updateSelectedServiceNodes();
            if (this.selectedService) {
                this.realmNodesGrid.setData(_.sortBy(this.selectedService.realmNodes, 'nodeName'));
                this.projectsRequestInfo.data = { handle: this.selectedService.serviceId, systemScope: true };
            }
        } else {
            this.projectsRequestInfo.data = null;
        }
    }

    ngOnDestroy(): void {
        this.realmNodeSubscription.unsubscribe();
        this.entityChangedEvent.unsubscribe();
    }

    refreshProject(): void {
        this.restService.post(Endpoint.realmManager_listProjects, { systemScope: true, contextHandle: 'noHandle' }).then(servicesResponse => {
            if (servicesResponse) {
                if (servicesResponse.services?.length > 0) {
                    this.services = _.reject(servicesResponse.services, service => service.serviceName === 'Internal Digital Reef Service');
                    this.servicesGrid.setData(this.services, false, true);
                }
                if (this.project && servicesResponse.projects?.length > 0) {
                    this.currentService = _.find(servicesResponse.projects, project => project.handle === this.project.handle)?.projectServiceName;
                }
                this.updateJobs(true);
            }
        });
    }

    preselectService(currentService: string): void {
        const selectedRow = this.servicesGrid.findNode(node => node.data.serviceName === currentService, true);
        if (selectedRow) {
            this.selectedService = selectedRow.data;
            this.servicesGrid.selectNode(selectedRow);
            this.servicesGrid.scrollElementIntoView();
        }
    }

    onSelectionChanged(event: SelectionChangedEvent): void {
        this.selectedService = event.api.getSelectedRows()[0];
        if (this.selectedService) {
            this.updateSelectedServiceNodes();
            this.realmNodesGrid.setData(_.sortBy(this.selectedService.realmNodes, 'nodeName'));
            this.projectsRequestInfo.data = { handle: this.selectedService.serviceId, systemScope: true };
            this.projectsGrid.loadData();
        }
    }

    updateSelectedServiceNodes(): void {
        if (this.allNodes?.length) {
            this.updateJobs();
            this.selectedService?.realmNodes.forEach(element => {
                if (!element.analyticNodeMode) {
                    element.analyticNodeMode = this.allNodes.find(node => node.name === element.nodeName).analyticNodeMode;
                }
            });
        }
    }

    updateJobs(refresh = false): void {
        const optionalCall = refresh ? (() => this.restService.post(Endpoint.realmManager_listJobs, { systemScope: true })) :
            () => Promise.resolve(this.allJobs);
        optionalCall().then(response => {
            if (response?.jobs) {
                response.jobs.forEach(job => job = new Job(job));
            }
            this.allJobs = response?.jobs || this.allJobs;
            if (this.selectedService && this.allJobs) {
                this.jobsGrid.setData(_.filter(this.allJobs, job => job.serviceName === this.selectedService.serviceName));
            }
        });
    }

    projectsCallback = (response: any) => {
        if (response?.projects) {
            response.projects = _.sortBy(response.projects, 'name');
        }
    }

    setTable(table: ServiceTable) {
        this.activeTable = table;
    }

    isPrimaryBtnEnabled(): boolean {
        return this.selectedService && this.selectedService.serviceName !== this.currentService;
    }

    saveChanges(): void {
        if (this.project) {
            this.restService.post(Endpoint.projectManager_updateProjectService, {
                projectHandle: this.project.handle,
                serviceDefinitionHandle: this.selectedService.serviceId,
                systemScope: true
            }).then(response => {
                this.commonService.sendMessage(MessageType.updateSystemSettingsProjectList);
            });
        } else {
            if (this.projectHandles) {
                this.restService.post(Endpoint.projectManager_updateMultipleProjectsService, {
                    projectHandles: this.projectHandles,
                    serviceDefinitionHandle: this.selectedService.serviceId,
                    systemScope: true
                })
            }
        }
        this.closeModal(true);
    }
}